import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { track } from "@/lib/analytics";
import { GENERIC_BOKADIREKT } from "@/lib/booking";
import { jsonLd, SITE_URL, faqSchema, breadcrumbSchema, absUrl } from "@/lib/seo";

const CANONICAL = `${SITE_URL}/produkter`;
const TITLE = "Barberprodukter: hår, skägg & vård | Tailor's Barber";
const DESCRIPTION =
  "Barberprodukter från Nōberu, System 4 och Tailor's egna serie. Pomade, skäggolja, schampo och rakvård. Köp i salong eller läs vår guide och boka online.";

import {
  PRODUCTS,
  PRODUCT_BY_KEY,
  CATEGORY_META,
  BRANDS,
  SCENTS,
  HOLD_LEVELS,
  type Product,
  type ProductKey,
} from "@/data/products";


interface QuizStep {
  id: string;
  q: string;
  parentValue?: string;
  options: readonly { label: string; value: string }[];
}

const QUIZ: readonly QuizStep[] = [
  {
    id: "category",
    q: "Vad letar du efter?",
    options: [
      { label: "Hårprodukt – styling & hållning", value: "haar" },
      { label: "Skäggprodukt – olja, balsam eller tvätt", value: "skagg" },
      { label: "Schampo & vård", value: "vard" },
    ],
  },
  {
    id: "haar-langd",
    parentValue: "haar",
    q: "Hur långt är ditt hår?",
    options: [
      { label: "Kort (0–5 cm)", value: "kort" },
      { label: "Medium (5–10 cm)", value: "medel" },
      { label: "Långt (10+ cm)", value: "langt" },
    ],
  },
  {
    id: "haar-typ",
    parentValue: "haar",
    q: "Hur ser ditt hår ut?",
    options: [
      { label: "Rakt och fint/tunt", value: "fint" },
      { label: "Rakt och tjockt/normalt", value: "tjockt" },
      { label: "Vågigt", value: "vagigt" },
      { label: "Krulligt", value: "krulligt" },
    ],
  },
  {
    id: "haar-finish",
    parentValue: "haar",
    q: "Vilken finish föredrar du?",
    options: [
      { label: "Matt – naturlig, inte blankig", value: "matt" },
      { label: "Naturlig lyster", value: "naturlig" },
      { label: "Spelar ingen roll", value: "alla" },
    ],
  },
  {
    id: "skagg-langd",
    parentValue: "skagg",
    q: "Hur långt är ditt skägg?",
    options: [
      { label: "Stubb (1–5 mm)", value: "stubb" },
      { label: "Kort skägg (5–20 mm)", value: "kort-skagg" },
      { label: "Medellångt skägg (2–5 cm)", value: "medel-skagg" },
      { label: "Långt skägg (5+ cm)", value: "langt-skagg" },
    ],
  },
  {
    id: "skagg-behov",
    parentValue: "skagg",
    q: "Vad är ditt primära behov?",
    options: [
      { label: "Mjuka upp och vårda", value: "vard" },
      { label: "Forma och kontrollera", value: "form" },
      { label: "Tvätta och rengöra", value: "tvätt" },
    ],
  },
  {
    id: "vard-typ",
    parentValue: "vard",
    q: "Vilket vårdbehov har du?",
    options: [
      { label: "Schampo för daglig tvätt", value: "schampo" },
      { label: "Mot mjäll eller irriterad hårbotten", value: "mjall" },
      { label: "Balsam för torrt eller längre hår", value: "balsam" },
      { label: "Tunnare hår – vill ha mer volym", value: "tunt" },
      { label: "Skäggschampo", value: "skagg-tvätt" },
    ],
  },
];

function getRecommendations(answers: Record<string, string>): ProductKey[] {
  const cat = answers["category"];

  if (cat === "haar") {
    const langd = answers["haar-langd"];
    const typ = answers["haar-typ"];
    const finish = answers["haar-finish"];

    if (typ === "krulligt") return ["curl-cream", "salt-water-spray"];
    if (typ === "vagigt") {
      if (langd === "langt" || langd === "medel") return ["salt-water-spray", "curl-cream"];
      return ["salt-water-spray", "fiber-pomade"];
    }
    if (langd === "langt") return ["salt-water-spray", "cream-pomade"];
    if (langd === "medel") {
      if (finish === "naturlig") return ["cream-pomade", "salt-water-spray"];
      return ["classic-paste", "salt-water-spray"];
    }
    // kort
    if (typ === "fint") return ["ocean-powder", "powder-wax"];
    if (finish === "blank") return ["shine-pomade"];
    if (finish === "naturlig") return ["cream-pomade", "tailors-gum"];
    if (typ === "tjockt") return ["matte-pomade", "strong-hold-wax"];
    return ["matte-pomade", "tailors-clay"];
  }

  if (cat === "skagg") {
    const langd = answers["skagg-langd"];
    const behov = answers["skagg-behov"];

    if (behov === "tvätt") {
      if (langd === "stubb" || langd === "kort-skagg") return ["beard-face-wash"];
      return ["beard-wash-jo", "beard-face-wash"];
    }
    if (langd === "stubb") return ["beard-oil-af", "beard-oil-feather"];
    if (langd === "kort-skagg") {
      if (behov === "vard") return ["beard-oil-af", "beard-tonic"];
      return ["beard-tonic", "beard-oil-af"];
    }
    // medel or long
    return ["beard-balm-bv", "beard-face-wash"];
  }

  if (cat === "vard") {
    const typ = answers["vard-typ"];
    if (typ === "mjall") return ["dandruff-shampoo"];
    if (typ === "tunt") return ["toppik-fibers"];
    if (typ === "balsam") return ["scalp-conditioner", "system4-hydro"];
    if (typ === "skagg-tvätt") return ["beard-wash-jo", "beard-face-wash"];
    return ["scalp-relax-shampoo"];
  }

  return ["matte-pomade"];
}

const FAQ = [
  {
    q: "Vilka produktmärken säljer Tailor's Barber?",
    a: "Vi säljer Nōberu of Sweden, Tailor's egna produktlinje, System 4, InShape, Toppik, Just for Men och flera andra märken. Bäst sortiment hittar du i vår salong på Åhléns City – hör med din barberare vad som passar dig bäst.",
  },
  {
    q: "Kan jag köpa produkter utan att boka klippning?",
    a: "Ja, välkommen in i salongen bara för att köpa produkter. Vår personal hjälper dig välja rätt baserat på din hårtyp och önskad finish.",
  },
  {
    q: "Vad är skillnaden mellan Tailor's egna produkter och Nōberu?",
    a: "Tailor's egna produktlinje (clay, pomade, beard balm, beard oil m.m.) är exklusivt framtagen för våra salonger. Nōberu of Sweden är ett skandinaviskt professionellt märke med brett sortiment. Barberarna kan vägleda dig till rätt val baserat på din hårtyp.",
  },
  {
    q: "Vad är skillnaden mellan pomade, wax och paste?",
    a: "Pomade ger flexibel hållning och är enkel att restylea. Wax har starkare hållning och funkar bra för tjockare hår. Paste ger en mer naturlig, texturerad look. Nōberu Matte Pomade och Tailor's Clay är bland våra mest sålda styling-produkter.",
  },
  {
    q: "Hur mycket produkt ska man ta?",
    a: "Tumregeln: börja med lite – en ärtlika mängd räcker ofta. Värm upp produkten i händerna, fördela jämnt och forma. Det är alltid lättare att lägga till mer än att ta bort.",
  },
];

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-foreground/80">
      {children}
    </span>
  );
}

function ProductQuiz() {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [subStep, setSubStep] = useState(0);

  const category = answers["category"];
  const branchSteps = useMemo(
    () => (category ? QUIZ.filter((s) => s.parentValue === category) : []),
    [category],
  );

  const totalSteps = 1 + branchSteps.length;
  const currentIndex = category ? 1 + subStep : 0;
  const done = category ? subStep >= branchSteps.length : false;

  function choose(id: string, value: string) {
    const next = { ...answers, [id]: value };
    setAnswers(next);
    if (id === "category") {
      setSubStep(0);
    } else {
      setSubStep((s) => s + 1);
    }
  }

  function reset() {
    setAnswers({});
    setSubStep(0);
  }

  function back() {
    if (!category) return;
    if (subStep === 0) {
      // back to category
      const { category: _drop, ...rest } = answers;
      void _drop;
      setAnswers(rest);
    } else {
      const prev = branchSteps[subStep - 1];
      const next = { ...answers };
      delete next[prev.id];
      setAnswers(next);
      setSubStep((s) => s - 1);
    }
  }

  if (done) {
    const recs = getRecommendations(answers).map((k) => PRODUCT_BY_KEY[k]);
    return (
      <div className="space-y-6">
        <div className="card-elev p-6 md:p-10">
          <div className="eyebrow">Din rekommendation</div>
          <h3 className="mt-2 text-3xl md:text-4xl">
            {recs.length > 1 ? "Produkter som passar dig" : "Produkt som passar dig"}
          </h3>
          <p className="mt-3 text-foreground/75 max-w-prose">
            Baserat på dina svar tror vi att följande {recs.length > 1 ? "produkter" : "produkt"} passar dig bäst.
            Fråga din barberare i salongen om du vill testa innan du köper.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {recs.map((p) => (
            <article key={p.key} className="card-elev p-6 md:p-8 flex flex-col">
              <ProductMedia p={p} className="h-40" />

              <div className="eyebrow">Rekommenderad</div>
              <h4 className="mt-2 text-2xl">{p.name}</h4>
              <p className="mt-1 text-brass-deep font-medium">{p.tagline}</p>
              <p className="mt-3 text-foreground/80 leading-relaxed">{p.desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Pill>Hållning: {p.holdLevel}</Pill>
                <Pill>Finish: {p.finish}</Pill>
                {p.bestFor.map((b) => (
                  <Pill key={b}>{b}</Pill>
                ))}
              </div>
              <div className="mt-5 rounded-xl bg-muted/50 border border-border p-4 text-sm text-foreground/80">
                <span className="text-brass-deep mr-2">✦</span>
                <span className="font-medium">Tips:</span> {p.tip}
              </div>
              <div className="mt-auto pt-6">
                <a
                  href={GENERIC_BOKADIREKT}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("book_now_click", { cta_location: "product_quiz_result" })
                  }
                  className="btn-brass w-full"
                >
                  Hitta produkter i salongen → Boka besök
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={reset} className="btn-outline">
            Gör om quizet
          </button>
        </div>
      </div>
    );
  }

  const currentStep = category ? branchSteps[subStep] : QUIZ[0];

  return (
    <div className="card-elev p-6 md:p-10">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Fråga {currentIndex + 1} av {totalSteps}
        </span>
        <div className="flex gap-1">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-6 rounded-full ${i <= currentIndex ? "bg-brass-deep" : "bg-muted"}`}
            />
          ))}
        </div>
      </div>
      <h3 className="mt-4 text-2xl md:text-3xl">{currentStep.q}</h3>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {currentStep.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => choose(currentStep.id, opt.value)}
            className="text-left px-5 py-4 rounded-xl border border-border hover:border-brass-deep hover:bg-muted/40 transition-colors"
          >
            {opt.label}
          </button>
        ))}
      </div>
      {currentIndex > 0 && (
        <button
          type="button"
          onClick={back}
          className="mt-6 text-sm text-muted-foreground hover:text-foreground"
        >
          ← Föregående
        </button>
      )}
    </div>
  );
}

function ProductMedia({ p, className }: { p: Product; className?: string }) {
  if (p.image) {
    return (
      <div className={`flex justify-center mb-4 bg-muted/20 rounded-lg overflow-hidden ${className ?? "h-36"}`}>
        <img
          src={p.image}
          alt={p.imageAlt ?? p.name}
          width={p.imageW}
          height={p.imageH}
          className="h-full w-auto object-contain p-2"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className={`flex flex-col items-center justify-center gap-1 mb-4 rounded-lg overflow-hidden border border-border bg-muted/40 text-center px-4 ${className ?? "h-36"}`}
    >
      <span className="text-xs uppercase tracking-[0.2em] text-brass-deep">{p.brand}</span>
      <span className="text-sm text-foreground/70 leading-snug line-clamp-2">{p.name}</span>
      <span className="text-[11px] text-muted-foreground">Foto i salongen</span>
    </div>
  );
}

function ProductCard({ p }: { p: Product }) {
  return (
    <article className="card-elev p-5 md:p-6 flex flex-col h-full">
      <ProductMedia p={p} />

      <div className="text-xs text-muted-foreground">{p.brand}</div>
      <h3 className="text-lg leading-snug">{p.name}</h3>
      <p className="mt-1 text-sm text-brass-deep font-medium">{p.tagline}</p>
      <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{p.desc}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Pill>Hållning: {p.holdLevel}</Pill>
        <Pill>Finish: {p.finish}</Pill>
        {p.scent && <Pill>Doft: {p.scent}</Pill>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {p.bestFor.map((b) => (
          <Pill key={b}>{b}</Pill>
        ))}
      </div>
    </article>
  );
}

const NO_SCENT = "__none__";

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${
        active
          ? "bg-brass-deep text-white border border-brass-deep"
          : "border border-border hover:border-brass-deep"
      }`}
    >
      {children}
    </button>
  );
}

function FilterGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  selected: readonly string[];
  onToggle: (value: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground w-24 shrink-0">
        {label}
      </span>
      {options.map((o) => (
        <Chip key={o.value} active={selected.includes(o.value)} onClick={() => onToggle(o.value)}>
          {o.label}
        </Chip>
      ))}
    </div>
  );
}

function ProductCatalog() {
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [scents, setScents] = useState<string[]>([]);
  const [holds, setHolds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeCount =
    categories.length + brands.length + scents.length + holds.length + (search.trim() ? 1 : 0);
  const isFiltered = activeCount > 0;

  function toggle(setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }

  function clearAll() {
    setCategories([]);
    setBrands([]);
    setScents([]);
    setHolds([]);
    setSearch("");
  }

  const matchesBase = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      if (categories.length && !categories.includes(p.category)) return false;
      if (brands.length && !brands.includes(p.brand)) return false;
      if (scents.length) {
        const val = p.scent ?? NO_SCENT;
        if (!scents.includes(val)) return false;
      }
      if (q) {
        const hay = [p.name, p.brand, p.tagline, p.desc, ...p.bestFor].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [categories, brands, scents, search]);

  const matches = useMemo(
    () => (holds.length ? matchesBase.filter((p) => holds.includes(p.holdLevel)) : matchesBase),
    [matchesBase, holds],
  );

  const holdOptions = useMemo(
    () =>
      HOLD_LEVELS.filter(
        (h) => holds.includes(h) || matchesBase.some((p) => p.holdLevel === h),
      ).map((h) => ({ value: h, label: h })),
    [matchesBase, holds],
  );

  const grouped = useMemo(() => {
    return (Object.keys(CATEGORY_META) as Product["category"][]).map((cat) => ({
      cat,
      meta: CATEGORY_META[cat],
      items: PRODUCTS.filter((p) => p.category === cat),
    }));
  }, []);

  const flat = useMemo(
    () =>
      [...matches].sort(
        (a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name),
      ),
    [matches],
  );

  return (
    <div>
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur border-b border-border -mx-4 px-4 py-3 md:mx-0 md:px-0 md:py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök produkt, märke eller behov…"
            aria-label="Sök bland produkter"
            className="w-full md:max-w-xs rounded-full border border-border bg-background px-4 py-2 text-sm focus:outline-none focus:border-brass-deep"
          />
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="md:hidden self-start rounded-full border border-border px-4 py-2 text-sm"
            aria-expanded={mobileOpen}
          >
            Filter{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
        </div>

        <div className={`${mobileOpen ? "block" : "hidden"} md:block mt-3 space-y-2`}>
          <FilterGroup
            label="Kategori"
            options={(Object.keys(CATEGORY_META) as Product["category"][]).map((c) => ({
              value: c,
              label: CATEGORY_META[c].label.split("–")[0].trim(),
            }))}
            selected={categories}
            onToggle={(v) => toggle(setCategories, v)}
          />
          <FilterGroup
            label="Varumärke"
            options={BRANDS.map((b) => ({ value: b, label: b }))}
            selected={brands}
            onToggle={(v) => toggle(setBrands, v)}
          />
          <FilterGroup
            label="Doft"
            options={[
              ...SCENTS.map((s) => ({ value: s, label: s })),
              { value: NO_SCENT, label: "Doftfri / ingen doft" },
            ]}
            selected={scents}
            onToggle={(v) => toggle(setScents, v)}
          />
          <FilterGroup
            label="Hållning"
            options={holdOptions}
            selected={holds}
            onToggle={(v) => toggle(setHolds, v)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 py-5 text-sm">
        <span className="text-muted-foreground">
          {isFiltered ? matches.length : PRODUCTS.length} av {PRODUCTS.length} produkter
        </span>
        {isFiltered && (
          <button
            type="button"
            onClick={clearAll}
            className="text-brass-deep underline underline-offset-4 hover:opacity-80"
          >
            Rensa filter
          </button>
        )}
      </div>

      {!isFiltered ? (
        <div className="space-y-14">
          {grouped.map(({ cat, meta, items }) => (
            <div key={cat} id={meta.slug}>
              <div className="max-w-2xl mb-6">
                <div className="eyebrow">{items.length} produkter</div>
                <h2 className="mt-2 text-2xl md:text-3xl">{meta.label}</h2>
                <p className="mt-2 text-foreground/70">{meta.blurb}</p>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <ProductCard key={p.key} p={p} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : flat.length === 0 ? (
        <div className="card-elev p-8 text-center">
          <h2 className="text-2xl">Inga produkter matchar</h2>
          <p className="mt-2 text-foreground/70">
            Prova att ta bort ett filter eller söka på något bredare.
          </p>
          <button type="button" onClick={clearAll} className="btn-outline mt-5">
            Rensa filter
          </button>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {flat.map((p) => (
            <ProductCard key={p.key} p={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function Page() {
  return (
    <SiteShell>
      <section className="container-page pt-10 md:pt-16 pb-6">
        <nav className="text-xs text-muted-foreground mb-6">
          <Link to="/" className="hover:underline">
            Hem
          </Link>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Produkter</span>
        </nav>
        <div className="max-w-3xl">
          <div className="eyebrow">Sortiment i salongen</div>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Produkter för hår, skägg &amp; vård
          </h1>
          <p className="mt-5 text-lg text-foreground/75">
            Barber produkter för hår, skägg och vård: ett handplockat sortiment i salongen från
            Nōberu of Sweden, Tailor's egen serie, System 4, InShape, Toppik, Just For Men och
            American Crew. Bläddra hela sortimentet nedan, eller hoppa till{" "}
            <a href="#quiz" className="underline underline-offset-4">quizet</a> så matchar vi dig med
            rätt produkt.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            {Object.values(CATEGORY_META).map((meta) => (
              <a
                key={meta.slug}
                href={`#${meta.slug}`}
                className="rounded-full border border-border px-4 py-2 hover:border-brass-deep transition-colors"
              >
                {meta.label.split("–")[0].trim()}
              </a>
            ))}
            <a href="#quiz" className="rounded-full bg-brass-deep text-white px-4 py-2 hover:opacity-90 transition-opacity">Gör produktquizet</a>
          </div>
        </div>
      </section>

      <section className="container-page py-10 md:py-14">
        <ProductCatalog />
      </section>

      <section id="quiz" className="container-page py-12 md:py-16 border-t border-border/60">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow">Personlig rekommendation</div>
          <h2 className="mt-3 text-3xl md:text-4xl">Vilken produkt passar mig?</h2>
          <p className="mt-3 text-foreground/70">
            Berätta vad du letar efter så föreslår vi en produkt som passar din hårtyp, skägglängd
            eller vårdbehov.
          </p>
        </div>
        <ProductQuiz />
      </section>

      <section className="container-page py-12">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow">Eller fråga din barberare</div>
          <h2 className="mt-3 text-3xl md:text-4xl">Personlig rådgivning i salongen</h2>
          <p className="mt-3 text-foreground/70">
            Vår personal hjälper dig hitta rätt produkt baserat på ditt hår, skägg och stilmål.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Link to="/herrklippning/" className="card-elev p-6 hover:border-brass-deep transition-colors">
            <div className="eyebrow">Boka</div>
            <h3 className="mt-2 text-xl">Herrklippning</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Vi rekommenderar rätt produkt efter klippningen.
            </p>
          </Link>
          <Link to="/skaggtrimning/" className="card-elev p-6 hover:border-brass-deep transition-colors">
            <div className="eyebrow">Boka</div>
            <h3 className="mt-2 text-xl">Skäggtrimning</h3>
            <p className="text-sm text-muted-foreground mt-1">Personlig vård för ditt skägg.</p>
          </Link>
          <a
            href={GENERIC_BOKADIREKT}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("book_now_click", { cta_location: "products_page" })}
            className="card-elev p-6 hover:border-brass-deep transition-colors"
          >
            <div className="eyebrow">Boka</div>
            <h3 className="mt-2 text-xl">Boka tid online</h3>
            <p className="text-sm text-muted-foreground mt-1">Se lediga tider hos alla barberare.</p>
          </a>
        </div>
      </section>

      <Faq items={FAQ} heading="Vanliga frågor om groomingprodukter" />
    </SiteShell>
  );
}

// No ItemList/Product structured data is emitted on this page: the catalogue
// shows no prices (it lists what the salons stock, not a shop), so Product
// nodes would violate Google's requirement that every Product carries an
// offers, review or aggregateRating. Rich results were never attainable
// without offers, so nothing is lost by omitting the typing. FAQPage,
// BreadcrumbList and Organization schema remain.

export const Route = createFileRoute("/produkter")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absUrl(CANONICAL) },
    ],
    links: [{ rel: "canonical", href: absUrl(CANONICAL) }],
    scripts: [
      jsonLd(faqSchema(FAQ)),
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Produkter", url: "/produkter" },
        ]),
      ),
    ],
  }),
  component: Page,
});
